/** Row ⇄ widget conversions for level=tag feature-flag rows.
 *
 * The chip conversions themselves are shared with every other resource that
 * owns a tag condition (see components/TagConditionInput/convert.ts); this
 * module only binds them to the flag row shape. */
import type { FeatureFlagListItem, FlagTagWrite } from '@/api/notificationFeatures';
import {
  chipSummaryLabels,
  fromWireChips,
  toWireChips,
  type TagConditionValue,
  type WireTagChipRead,
} from '@/components/TagConditionInput';

/** Widget value from a grid row. */
export function fromFlagRow(row: FeatureFlagListItem): TagConditionValue {
  return fromWireChips(row.tags as WireTagChipRead[] | null, row.tag_match_mode);
}

/** Widget chips → the write contract's chip shape ({tag_id} → {tag: id}). */
export function toCreateTags(value: TagConditionValue): FlagTagWrite[] {
  return toWireChips(value) as FlagTagWrite[];
}

export interface TagRowSummary {
  /** Per-chip display label: "wan · VIP · gold" (pinned) / "vip · gold" (by code). */
  labels: string[];
  mode: 'and' | 'or';
  sources: string[];
}

/** Compact description of a tag row — shared by the grid's ScopeCell and its
 *  tooltip so the two never drift. */
export function summarizeTagRow(row: FeatureFlagListItem): TagRowSummary {
  const value = fromFlagRow(row);
  return {
    labels: chipSummaryLabels(value),
    mode: value.matchMode,
    sources: row.tag_sources ?? [],
  };
}
